/**
 * Gift matching pools API
 *
 *   GET    /api/v2/GiftMatchingPool                                  every pool, newest first
 *   GET    /api/v2/GiftMatchingPool/{id}                             one pool
 *   GET    /api/v2/GiftMatchingPool/campaign/{campaignId}            a campaign's organisation, currency and gift matches
 *   POST   /api/v2/GiftMatchingPool                                  create a pool from a source gift match, taking its settings
 *   PUT    /api/v2/GiftMatchingPool/{id}                             update the pool settings on every linked gift match
 *   POST   /api/v2/GiftMatchingPool/{id}/campaigns                   add campaigns
 *   DELETE /api/v2/GiftMatchingPool/{id}/campaigns/{giftMatchingId}  remove a campaign, keeping or removing its gift match
 *
 * Every endpoint requires a system admin credential.
 *
 *   POST   /api/v2/page/campaign/search                              campaigns matching a search, for the picker
 */

import constants from '../../../constants';
import {
  CampaignSearchResult,
  CampaignSummary,
  GiftMatchingPool,
  PoolCreation,
  PoolSettings
} from './types';

const ENDPOINT = `${constants.baseApiHost}/api/v2/GiftMatchingPool`;
const CAMPAIGN_SEARCH_ENDPOINT = `${constants.baseApiHost}/api/v2/page/campaign/search`;
const campaignSearchPageSize = 20;

/**
 * A failed request. `codes` carries the API's error codes, such as `currency.invalid`, so the page
 * can say what was refused.
 */
export class GiftMatchingPoolApiError extends Error {
  codes: string[];

  constructor(message: string, codes: string[] = []) {
    super(message);
    this.codes = codes;
  }
}

const getSession = () => {
  const raw = localStorage['session'];
  if (!raw || raw === 'undefined') {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: constants.apikey || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

const handleApiResponse = async <T>(
  response: Response,
  failureMessage: string
): Promise<T> => {
  if (response.status === 204) {
    return undefined as T;
  }
  let json: {
    success?: boolean;
    data?: T;
    error?: { error?: string }[] | null;
  } | null = null;
  try {
    json = await response.json();
  } catch {
    throw new GiftMatchingPoolApiError(failureMessage);
  }
  if (!response.ok || !json?.success) {
    const codes = (json?.error ?? [])
      .map((item) => item?.error)
      .filter((code): code is string => !!code);
    throw new GiftMatchingPoolApiError(failureMessage, codes);
  }
  return json.data as T;
};

export const searchCampaigns = async (
  text: string,
  organizationId?: number | null
): Promise<CampaignSearchResult[]> => {
  const response = await fetch(CAMPAIGN_SEARCH_ENDPOINT, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({
      page: 1,
      pageSize: campaignSearchPageSize,
      resultType: 'Full',
      text,
      orderBy: { key: 'name', direction: 'asc' },
      filters: organizationId
        ? [
            {
              key: 'organizationId',
              operator: 'EqualTo',
              value: organizationId
            }
          ]
        : []
    })
  });
  const data = await handleApiResponse<{ list?: CampaignSearchResult[] }>(
    response,
    'campaign search failed'
  );
  return data?.list ?? [];
};

export const getGiftMatchingPools = async (): Promise<GiftMatchingPool[]> => {
  const response = await fetch(ENDPOINT, { headers: getHeaders() });
  return handleApiResponse<GiftMatchingPool[]>(
    response,
    'gift matching pools request failed'
  );
};

export const getGiftMatchingPool = async (
  id: number
): Promise<GiftMatchingPool> => {
  const response = await fetch(`${ENDPOINT}/${id}`, { headers: getHeaders() });
  return handleApiResponse<GiftMatchingPool>(
    response,
    'gift matching pool request failed'
  );
};

export const getPoolCampaignSummary = async (
  campaignId: number
): Promise<CampaignSummary> => {
  const response = await fetch(`${ENDPOINT}/campaign/${campaignId}`, {
    headers: getHeaders()
  });
  return handleApiResponse<CampaignSummary>(
    response,
    'gift matching pool campaign request failed'
  );
};

export const createGiftMatchingPool = async (
  model: PoolCreation
): Promise<GiftMatchingPool> => {
  const response = await fetch(ENDPOINT, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(model)
  });
  return handleApiResponse<GiftMatchingPool>(
    response,
    'creating the gift matching pool failed'
  );
};

export const updateGiftMatchingPool = async (
  id: number,
  model: PoolSettings
): Promise<GiftMatchingPool> => {
  const response = await fetch(`${ENDPOINT}/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(model)
  });
  return handleApiResponse<GiftMatchingPool>(
    response,
    'updating the gift matching pool failed'
  );
};

export const addGiftMatchingPoolCampaigns = async (
  id: number,
  campaignIds: number[]
): Promise<GiftMatchingPool> => {
  const response = await fetch(`${ENDPOINT}/${id}/campaigns`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ campaignIds })
  });
  return handleApiResponse<GiftMatchingPool>(
    response,
    'adding campaigns to the gift matching pool failed'
  );
};

export const removeGiftMatchingPoolCampaign = async (
  id: number,
  giftMatchingId: number,
  keepGiftMatching: boolean
): Promise<void> => {
  const response = await fetch(
    `${ENDPOINT}/${id}/campaigns/${giftMatchingId}?keepGiftMatching=${keepGiftMatching}`,
    { method: 'DELETE', headers: getHeaders() }
  );
  await handleApiResponse<unknown>(
    response,
    'removing the campaign from the gift matching pool failed'
  );
};
